import { ACTIVITIES, SHIFT_TYPES, useActivities } from "../lib/activities";
import { NewActivityButton } from "./NewActivity";

/**
 * Matrice de saisie des tarifs : 8 activités × 4 types de vacation.
 * value: { activityCode: { regular, night, holiday, sunday } }
 * placeholders: même structure pour afficher les tarifs hérités en grisé
 * onChange(activityCode, shiftCode, rawValue)
 */
function fmt3(raw) {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(3);
}

export default function RatesMatrix({ value, placeholders, onChange, testIdPrefix = "rate" }) {
  const acts = useActivities();
  const cell = (activity, shift) => {
    const v = value?.[activity]?.[shift];
    const ph = placeholders?.[activity]?.[shift];
    return (
      <input
        data-testid={`${testIdPrefix}-${activity}-${shift}`}
        type="number"
        step="0.001"
        min="0"
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 bg-white"
        placeholder={ph != null ? Number(ph).toFixed(3) : "—"}
        value={v ?? ""}
        onChange={(e) => onChange(activity, shift, e.target.value)}
        onBlur={(e) => {
          const formatted = fmt3(e.target.value);
          if (formatted !== e.target.value) onChange(activity, shift, formatted);
        }}
      />
    );
  };

  return (
    <div className="overflow-x-auto -mx-2 sm:mx-0">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <th className="text-left pb-2 pr-2 sticky left-0 bg-inherit">Activité</th>
            {SHIFT_TYPES.map((s) => (
              <th key={s.code} className="pb-2 px-1 text-center font-bold">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {acts.map((a) => (
            <tr key={a.code} className="border-t border-slate-100">
              <td className="py-1.5 pr-2 font-semibold text-slate-700 whitespace-nowrap text-xs sticky left-0 bg-inherit">
                {a.label}
              </td>
              {SHIFT_TYPES.map((s) => (
                <td key={s.code} className="py-1.5 px-1 w-20">
                  {cell(a.code, s.code)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2"><NewActivityButton testid={`${testIdPrefix}-new-activity`} /></div>
    </div>
  );
}

const codesOf = (obj) => [...new Set([...ACTIVITIES.map((a) => a.code), ...Object.keys(obj || {})])];

// Helpers de sérialisation
export function emptyRatesMatrix() {
  const out = {};
  for (const a of ACTIVITIES) {
    out[a.code] = { regular: "", night: "", holiday: "", sunday: "" };
  }
  return out;
}

// Convertit une matrice {activity: {shift: "" | num}} en payload backend
// (null si toutes les cellules sont vides ; sinon objet avec uniquement les nombres définis).
export function serializeRatesMatrix(matrix) {
  const out = {};
  let hasAny = false;
  for (const code of codesOf(matrix)) {
    const a = { code };
    const cells = matrix?.[a.code] || {};
    const row = {};
    let rowHas = false;
    for (const s of SHIFT_TYPES) {
      const raw = cells[s.code];
      if (raw === "" || raw === null || raw === undefined) {
        row[s.code] = null;
      } else {
        const n = Number(raw);
        row[s.code] = Number.isFinite(n) ? n : null;
        if (Number.isFinite(n)) rowHas = true;
      }
    }
    if (rowHas) {
      out[a.code] = row;
      hasAny = true;
    }
  }
  return hasAny ? out : null;
}

// Convertit un payload backend en matrice pour l'UI
export function deserializeRatesMatrix(payload) {
  const out = emptyRatesMatrix();
  if (!payload || typeof payload !== "object") return out;
  for (const code of codesOf(payload)) {
    const src = payload[code];
    if (!src || typeof src !== "object") continue;
    out[code] = out[code] || { regular: "", night: "", holiday: "", sunday: "" };
    for (const s of SHIFT_TYPES) {
      out[code][s.code] = src[s.code] ?? "";
    }
  }
  return out;
}
